import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft, Search, X } from "lucide-react";
import { toast } from "sonner";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import ChatSelector from "@/components/ChatSelector";
import SyncedChatPanel from "@/components/SyncedChatPanel";
import AiChatPanel from "@/components/AiChatPanel";

interface WhatsAppMessage {
  id: string;
  timestamp: number;
  sender: string;
  content: string;
  isSent: boolean;
  attachment?: any;
}

interface ChatData {
  id: string;
  file_name: string;
  messages: WhatsAppMessage[];
}

const MultiView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [selectedChats, setSelectedChats] = useState<ChatData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [hoveredTime, setHoveredTime] = useState<number | null>(null);
  const panelRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }

    const chatIds = searchParams.get("chats")?.split(",") || [];
    if (chatIds.length === 0) {
      navigate("/history");
      return;
    }

    loadChats(chatIds);
  }, [user, searchParams]);

  const loadChats = async (chatIds: string[]) => {
    try {
      setLoading(true);
      const chatsData: ChatData[] = [];

      for (const chatId of chatIds) {
        const { data, error } = await supabase
          .from("chat_analyses")
          .select("*")
          .eq("id", chatId)
          .eq("user_id", user!.id)
          .single();

        if (error) throw error;

        const messages = (data.messages_data as any[]) || [];
        const parsedMessages = messages.map((msg: any, index: number) => ({
          id: `${chatId}-${index}`,
          timestamp: msg.timestamp ? new Date(msg.timestamp).getTime() : 0,
          sender: msg.sender || "",
          content: msg.content || "",
          isSent: msg.isSent || false,
          attachment: msg.attachment,
        }));

        chatsData.push({
          id: chatId,
          file_name: data.file_name,
          messages: parsedMessages,
        });
      }

      setSelectedChats(chatsData);
    } catch (error) {
      console.error("Error loading chats:", error);
      toast.error(t("toast.loadFailed") || "Failed to load chats");
    } finally {
      setLoading(false);
    }
  };

  const allMessages = useMemo(() => {
    const combined = selectedChats.flatMap(chat =>
      chat.messages.map(msg => ({ ...msg, chatId: chat.id }))
    );
    return combined.sort((a, b) => a.timestamp - b.timestamp);
  }, [selectedChats]);

  const timelineRange = useMemo(() => {
    if (allMessages.length === 0) return { min: 0, max: 0 };
    return {
      min: allMessages[0].timestamp,
      max: allMessages[allMessages.length - 1].timestamp,
    };
  }, [allMessages]);

  // Initialize currentTime to min when messages load
  useEffect(() => {
    if (allMessages.length > 0 && currentTime === 0) {
      setCurrentTime(timelineRange.min);
    }
  }, [allMessages, timelineRange.min]);

  // Calculate message density for heat map (bucket messages into time intervals)
  const messageDensity = useMemo(() => {
    if (allMessages.length === 0) return [];
    
    const buckets = 100; // Number of buckets for the heat map
    const timeSpan = timelineRange.max - timelineRange.min;
    const bucketSize = timeSpan / buckets;
    
    const density = new Array(buckets).fill(0);
    
    allMessages.forEach(msg => {
      const bucketIndex = Math.floor((msg.timestamp - timelineRange.min) / bucketSize);
      if (bucketIndex >= 0 && bucketIndex < buckets) {
        density[bucketIndex]++;
      }
    });
    
    // Normalize to 0-1 range
    const maxDensity = Math.max(...density, 1);
    return density.map((count, index) => ({
      density: count / maxDensity,
      timestamp: timelineRange.min + (index * bucketSize) + (bucketSize / 2) // Middle of bucket
    }));
  }, [allMessages, timelineRange]);

  const handleTimelineChange = (value: number[]) => {
    const timestamp = value[0];
    setCurrentTime(timestamp);
    scrollToTime(timestamp);
  };

  const handleHeatMapClick = (timestamp: number) => {
    setCurrentTime(timestamp);
    scrollToTime(timestamp);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (!query) return;

    const matchingMessage = allMessages.find(msg =>
      msg.content.toLowerCase().includes(query.toLowerCase())
    );

    if (matchingMessage) {
      setCurrentTime(matchingMessage.timestamp);
      scrollToTime(matchingMessage.timestamp);
    }
  };

  const scrollToTime = (timestamp: number) => {
    selectedChats.forEach(chat => {
      // Find the nearest message to the target timestamp
      let nearestMessage = chat.messages[0];
      let minDiff = Math.abs(chat.messages[0].timestamp - timestamp);
      
      chat.messages.forEach(msg => {
        const diff = Math.abs(msg.timestamp - timestamp);
        if (diff < minDiff) {
          minDiff = diff;
          nearestMessage = msg;
        }
      });
      
      const messageEl = document.getElementById(`msg-${chat.id}-${nearestMessage.timestamp}`);
      if (messageEl) {
        messageEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  };

  const handleTimeSync = (timestamp: number) => {
    setCurrentTime(timestamp);
    scrollToTime(timestamp);
  };

  const combinedContext = useMemo(() => {
    return selectedChats.map(chat => 
      `Chat: ${chat.file_name}\n${chat.messages.map(m => `${m.sender}: ${m.content}`).join('\n')}`
    ).join('\n\n---\n\n');
  }, [selectedChats]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t("viewer.loading") || "Loading..."}</p>
        </div>
      </div>
    );
  }

  if (selectedChats.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-subtle p-8">
        <ChatSelector onSelect={(ids) => navigate(`/multi-view?chats=${ids.join(",")}`)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <div className="bg-card border-b shadow-soft sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-4 mb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/history")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("viewer.back") || "Back"}
            </Button>
            <h1 className="text-xl font-bold flex-1">
              {t("multiView.title") || "Multi View"} ({selectedChats.length} {t("multiView.chats") || "chats"})
            </h1>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t("multiView.searchPlaceholder") || "Search across all chats..."}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchQuery("")}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Multi-panel view */}
      <div className="container mx-auto p-4 h-[calc(100vh-230px)]">
        <ResizablePanelGroup direction="horizontal" className="rounded-lg border">
          {selectedChats.map((chat, index) => (
            <>
              <ResizablePanel key={chat.id} defaultSize={100 / selectedChats.length}>
                <SyncedChatPanel
                  chat={chat}
                  currentTime={currentTime}
                  searchQuery={searchQuery}
                  onTimeSync={handleTimeSync}
                />
              </ResizablePanel>
              {index < selectedChats.length - 1 && (
                <ResizableHandle withHandle />
              )}
            </>
          ))}
        </ResizablePanelGroup>
      </div>

      {/* Timeline Slider */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t shadow-lg z-20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              {/* Heat map visualization with time position */}
              <div className="relative h-8 mb-3 rounded overflow-hidden bg-muted">
                {messageDensity.map((bucket, index) => {
                  // Reverse the position: rightmost = oldest
                  const reversedIndex = messageDensity.length - 1 - index;
                  return (
                    <div
                      key={index}
                      className="absolute top-0 bottom-0 transition-colors cursor-pointer hover:opacity-80"
                      style={{
                        left: `${(reversedIndex / messageDensity.length) * 100}%`,
                        width: `${100 / messageDensity.length}%`,
                        backgroundColor: bucket.density > 0 
                          ? `hsl(var(--primary) / ${Math.max(bucket.density * 0.8, 0.1)})` 
                          : 'transparent',
                      }}
                      onMouseEnter={() => setHoveredTime(bucket.timestamp)}
                      onMouseLeave={() => setHoveredTime(null)}
                      onClick={() => handleHeatMapClick(bucket.timestamp)}
                    />
                  );
                })}
                
                {/* Current time position indicator (reversed) */}
                {currentTime > 0 && timelineRange.max > timelineRange.min && (
                  <div
                    className="absolute top-0 bottom-0 w-0.5 bg-primary z-10 pointer-events-none"
                    style={{
                      left: `${100 - ((currentTime - timelineRange.min) / (timelineRange.max - timelineRange.min)) * 100}%`,
                    }}
                  >
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-primary rounded-full" />
                  </div>
                )}
                
                {/* Hover tooltip */}
                {hoveredTime && hoveredTime > 0 && (
                  <div
                    className="absolute -top-12 left-1/2 -translate-x-1/2 bg-popover text-popover-foreground px-3 py-2 rounded-md shadow-md text-sm whitespace-nowrap z-20 border pointer-events-none"
                    style={{
                      left: `${100 - ((hoveredTime - timelineRange.min) / (timelineRange.max - timelineRange.min)) * 100}%`,
                    }}
                  >
                    {format(new Date(hoveredTime), "MMM d, yyyy HH:mm")}
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {timelineRange.max > 0 ? format(new Date(timelineRange.max), "MMM d, yyyy HH:mm") : "--"}
                </span>
                {currentTime && currentTime > 0 && (
                  <span className="font-semibold text-primary">
                    {format(new Date(currentTime), "MMM d, yyyy HH:mm:ss")}
                  </span>
                )}
                <span>
                  {timelineRange.min > 0 ? format(new Date(timelineRange.min), "MMM d, yyyy HH:mm") : "--"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Panel - Always visible at bottom left */}
      <div className="fixed bottom-24 left-4 z-30">
        <AiChatPanel
          chatContext={combinedContext}
          language="en"
        />
      </div>
    </div>
  );
};

export default MultiView;
